import { createContext, useContext, useMemo } from 'react';
import type { ClassificationSchema, ClassificationVocabulary } from '../services/api';
import { resolveLabel, resolveColor, resolveAbbr, resolveCategoryNoun, resolveSecondaryAxisNoun, resolveSecondaryAxisNounPlural, resolveCategoriesForSecondaryAxisCode } from '../services/categoryMeta';
import { ownsMsbKVocabulary, isKnownRole, ROW_LAYOUT_DEFAULT_BY_ROLE, type KnownRole } from '../services/roleRegistry';
import { CATEGORY_KEYS, UNCLASSIFIED_KEY, formatSecondaryAxisCode, getReasonCodeLabel } from '../constants';

// gMSB default: all K codes except KU (mirrors the stats views' existing CATEGORY_KEYS.filter)
const GMSB_STAT_KEYS = CATEGORY_KEYS.filter(k => k !== UNCLASSIFIED_KEY) as string[];

export type RowLayout = 'strip_label' | 'strip_abbr';

// Role-aware default for the ticket-row category display, shared by App (effective default)
// and EinstellungenSeite (the toggle's shown default + changes-check) so they can't drift.
// The per-role answer itself is declared exhaustively in ROW_LAYOUT_DEFAULT_BY_ROLE
// (services/roleRegistry.ts) — this function's only job is the lookup plus the unresolved-role
// floor below. A saved operator override (UI_ROW_LAYOUT) wins over whatever this returns.
// Accepts string|null|undefined (not just KnownRole) because both callers are legitimate: App.tsx
// passes the already-validated KnownRole|null, EinstellungenSeite passes its own `role: string`
// prop verbatim.
export function defaultRowLayout(role: string | null | undefined): RowLayout {
  // An unresolved or unrecognised role gets the MAJORITY posture (three of four known roles), not
  // the single named exception's. vnb is the one role whose long marktprozess labels read better
  // as a row-2 badge; a role this function has never seen is not vnb, so it must not silently land
  // on vnb's posture just because that happened to be the fall-through branch — that exact
  // silent-inheritance shape is how a real role ended up on the wrong default before this map existed.
  return role && isKnownRole(role) ? ROW_LAYOUT_DEFAULT_BY_ROLE[role] : 'strip_abbr';
}

type Ctx = {
  schema: ClassificationSchema | null;
  // The role's classification vocabulary as DATA (BE /config). When present it WINS over every
  // path below it — that is what makes a label rename one schema edit instead of a code change,
  // and what lets a future schema-driven role need no frontend change at all.
  vocabulary?: ClassificationVocabulary | null;
  rowLayout: RowLayout;
  // The live role identity (Task 3's KnownRole|null). Optional/undefined ONLY for test doubles
  // written before role-threading existed — every real Provider mount (App.tsx) supplies it.
  // Governs the placeholder floor below: omitted here is read as "caller doesn't care about
  // role-gating" and keeps pre-existing K-default behaviour; explicit `null` is Task 3's real
  // "config never resolved" signal and gets the placeholder floor exactly like an unknown role.
  role?: KnownRole | null;
  // Mirrors App.tsx's own configSettled — false ONLY during the genuine first-ever /config fetch
  // of a session (no sessionStorage vocabulary cache to paint from yet). Omitted (every existing
  // test double written before this field existed) reads as settled/resolved, same convention as
  // `role` above — a caller that doesn't pass it is declaring "not testing the loading window",
  // never an implicit "still loading". Consumers read this via configLoading below — the
  // CONFIG-settle window, not a vocabulary-specific one — rather than reaching into this field
  // directly.
  configSettled?: boolean;
};
const SchemaCtx = createContext<Ctx>({ schema: null, vocabulary: null, rowLayout: 'strip_label' });
export const ClassificationSchemaProvider = SchemaCtx.Provider;

export function useCategoryMeta() {
  const { schema, vocabulary, rowLayout, role, configSettled } = useContext(SchemaCtx);
  // Genuinely "still resolving the config fetch" — distinct from vocabularyMissing below, which is
  // a TERMINAL state (config resolved, this role simply owns no schema/vocabulary). This is the
  // CONFIG-settle window, not a vocabulary-specific one: during it every vocab-derived string
  // (categoryNoun, label(), …) would otherwise fall through to the raw `{category.…}` placeholder
  // floor because role/schema/vocab are ALL still their neutral pre-fetch defaults — a
  // vocab-dependent consumer must render a skeleton instead of reading any of those accessors while
  // this is true (UI Layout Stability Rule: shimmer, never placeholder text); any other
  // config-dependent consumer reads the same signal for its own settle window. `configSettled ===
  // false` (not `!configSettled`) so an omitted/undefined flag — every test double written before
  // Task C — reads as settled, per this file's own role convention.
  const configLoading = configSettled === false;
  // Precedence, and the ONLY place it is decided: vocabulary → legacy schema → K-defaults.
  // gMSB reaches the K-defaults because it has NO vocabulary, not because anything asks its name —
  // its legacy rendering is preserved structurally rather than by a branch someone must remember.
  const vocab = vocabulary ?? null;
  // role omitted (an un-threaded test double) is read IDENTICALLY to role === null (Task 3's real
  // "config never resolved" signal) — never as an implicit gMSB request. The K-defaults are
  // gMSB/wMSB's OWN vocabulary, not a generic no-schema fallback, so no other role, known or not,
  // may silently inherit them; the arc's own rule ("no silent gmsb assumption anywhere") does not
  // carve out an exception for tests. An empty categoryKeys here is not silent: the only way it
  // happens is customerRole === null, the exact condition under which App.tsx already renders the
  // global RoleConfigWarning overlay — the warning IS on screen when the list goes empty.
  // RECONCILIATION enumeration: every value the tenant's primary axis carries, unclassified bucket included. A
  // total computed over these reconciles against the ticket count — rejection-flow heatmaps,
  // force-category pickers, anything that would silently lose rows by omitting a value.
  const categoryKeys = useMemo(
    () => vocab ? vocab.primary.values.map(v => v.id)
        : schema ? schema.marktprozess.map(v => v.id)
        : ownsMsbKVocabulary(role) ? GMSB_STAT_KEYS
        : [],
    [vocab, schema, role],
  );
  // CATEGORIZATION-PERFORMANCE enumeration: the same list minus every value the tenant marked
  // `excludedFromEvaluation`. The two sets are NOT interchangeable — an unclassified category ("could not
  // be classified") is not a classification outcome, so counting it distorts every rate computed
  // over the set, while dropping it from a reconciliation total silently loses rows.
  //
  // The marker is SERVED, and that is the point: this file used to encode the same fact as
  // `CATEGORY_KEYS.filter(k => k !== UNCLASSIFIED_KEY)` — correct only because gMSB happens to
  // spell its unclassified value 'KU'. A role whose unclassified value is spelled anything else needs no frontend change now.
  // GMSB_STAT_KEYS survives as the LEGACY-FLOOR arm only (no vocabulary served yet); the moment a
  // vocabulary arrives, the marker answers instead and the literal is never consulted.
  //
  // The other two arms are deliberately identical to categoryKeys': a legacy ClassificationSchema
  // carries no marker (nothing to drop), and the floor list already excludes gMSB's unclassified value. So the
  // two sets diverge in exactly ONE state — a served vocabulary that marks something — which is
  // why delivering vocabulary rows to an MSB tenant before this split existed would have flipped
  // every performance surface to include KU.
  const performanceCategoryKeys = useMemo(
    () => vocab ? vocab.primary.values.filter(v => !v.excludedFromEvaluation).map(v => v.id)
        : schema ? schema.marktprozess.map(v => v.id)
        : ownsMsbKVocabulary(role) ? GMSB_STAT_KEYS
        : [],
    [vocab, schema, role],
  );
  // The distinguishing flag stats surfaces must read before treating an empty categoryKeys as
  // "zero tickets": a KNOWN role (BE has told us who this tenant is) that does NOT own the legacy
  // K-vocabulary, with neither schema nor vocabulary loaded, is a role-configuration gap — e.g. a
  // freshly-provisioned VNB tenant before its taxonomy is seeded — not a data-volume fact. This is
  // reachable WITHOUT the global RoleConfigWarning (customerRole resolved fine; it's the tenant's
  // vocabulary/schema payload that's empty), so a stats surface that renders a generic "Keine
  // Daten" here would be indistinguishable from a real empty dataset — the silent-empty state the
  // placeholder floor forbids. `!!role && isKnownRole(role)` is defensive belt-and-braces: the Ctx
  // type already only allows KnownRole|null, but a drifted/`as any` role must not evaluate true.
  const vocabularyMissing = !!role && isKnownRole(role) && !ownsMsbKVocabulary(role) && !schema && !vocab;
  // Second axis. Vocabulary: the secondary axis' ids. VNB: anliegen ids from schema. gMSB has no
  // schema-anliegen — its second-axis option source is the REASON_CODES constant.
  const secondaryAxisKeys = useMemo(
    () => vocab ? (vocab.secondary?.values.map(v => v.id) ?? [])
        : schema ? schema.anliegen.map(v => v.id)
        : [],
    [vocab, schema],
  );
  // Label lookup built from the tenant's own data. Falls through to the legacy resolver — which
  // still backstops K-codes, H-codes and process types via CATEGORY_LABELS — so a value missing
  // from the vocabulary degrades to a readable German label, never a raw id.
  const vocabLabels = useMemo(() => {
    if (!vocab) return null;
    const m = new Map<string, string>();
    for (const v of vocab.primary.values) m.set(v.id, v.label);
    for (const v of vocab.secondary?.values ?? []) m.set(v.id, v.label);
    return m;
  }, [vocab]);
  return useMemo(() => {
    const label = (v: string) => vocabLabels?.get(v) ?? resolveLabel(schema, v, role);
    const color = (v: string) => resolveColor(schema, v);
    const abbr = (v: string) => resolveAbbr(vocab, v);
    // grund-code -> category-band mapping (D10 addendum 4), the seam counterpart of `label`. gMSB/
    // wMSB only today (`vocab.secondary` is the grund_code axis) — a schema-driven role has no
    // second-axis min/max concept, so `vocab` is the only source this reads; a legacy/schema-only
    // role falls straight through resolveCategoriesForSecondaryAxisCode's own floor.
    const categoriesForSecondaryAxisCode = (code: string) => resolveCategoriesForSecondaryAxisCode(vocab, code);
    // THE secondary-axis pair, and the only place either question is answered. The ruling is that
    // this axis renders de-slugged all-lowercase ALWAYS — never abbreviated, never dependent on
    // whether a vocabulary happens to be delivered. That independence is the point: a data
    // delivery must not restyle a live tenant, which is exactly what the retired label-mode lever
    // was invented to paper over.
    const secondaryAxisText = (code: string) => formatSecondaryAxisCode(code);
    // The hover carries the full proper-case wording, so abbreviating the face costs nothing.
    // Tier order MIRRORS `label`'s own (vocabulary → legacy schema → gMSB's baked catalog → the
    // raw code, never an invented word) — found missing its schema tier while wiring Task 4's
    // display surfaces onto this accessor: a VNB tenant on a pre-vocabulary backend (schema, no
    // vocab — a live, measured fleet state, not hypothetical) was falling straight from
    // vocabLabels (always empty there) to the gMSB reason-code catalog, floor-ing every anliegen
    // hover to its own raw id instead of the schema's resolved label. mp/an duplicate
    // resolveLabel's own schema lookup rather than calling it directly, because resolveLabel's
    // OWN floor (the `{category.…}` placeholder / CATEGORY_LABELS) is a different, gMSB/wMSB-
    // scoped floor than this accessor's (the raw code, unconditionally, never a borrowed word).
    const secondaryAxisHover = (code: string) =>
      vocabLabels?.get(code)
      ?? schema?.marktprozess.find(v => v.id === code)?.label
      ?? schema?.anliegen.find(v => v.id === code)?.label
      ?? getReasonCodeLabel(code);
    return {
      // The two SOURCE flags. Read one of these only when the answer genuinely depends on WHICH
      // source arrived; for "does this tenant label its categories from its own data?" — which is
      // what almost every call site is actually asking — read categoryLabelsFromData below.
      schemaActive: !!schema,
      vocabularyActive: !!vocab,
      // THE CALL-SITE DISCRIMINATOR — "does this tenant label its categories from its own data?"
      // Neither source flag alone is correct, and the asymmetry is measured, not hypothetical (live
      // fleet, 2026-08-05): a wim payload carries a vocabulary and a NULL legacy schema, while a vnb
      // tenant on a backend predating the vocabulary field carries a schema and NO vocabulary. Both
      // label from tenant data; only gMSB/wMSB, carrying neither, fall through to the K-defaults.
      // Gating on vocabulary alone would break this seam's own promise that an older backend behaves
      // exactly as it did before the field existed.
      //
      // ⚠ A short-lived presentation lever (UI_CATEGORY_LABEL_MODE / categoryLabelMode) once
      // conjoined a SECOND question here ("does this tenant WANT labels") and defaulted gmsb/wmsb/
      // wim to 'abbr' — which forced this flag false even on a wim tenant that has always served a
      // full vocabulary, sending five call sites down the gMSB K-code fallback path on a tenant that
      // owns none of it (Archiv badge, Statistik heatmap, ReanalysisModal options, the secondary-axis
      // filter, Uploads category options). The lever is deleted; this flag answers ONE question again.
      categoryLabelsFromData: !!vocab || !!schema,
      rowLayout,
      label,
      color,
      abbr,
      categoriesForSecondaryAxisCode,
      secondaryAxisText,
      secondaryAxisHover,
      // ── The two enumerations. Pick by the QUESTION, not by convenience (see their derivations
      // above): does this surface reconcile against a total, or does it measure categorization
      // performance? Getting it wrong is silent in both directions — a lost row, or a flattered rate.
      //
      // RECONCILIATION — every served value, unclassified bucket included.
      // VNB: marktprozess ids from schema. gMSB with vocabulary: K0–K4 + KU. gMSB on the legacy
      // floor (no vocabulary yet): K0–K4, because the floor list predates the marker.
      categoryKeys,
      // CATEGORIZATION PERFORMANCE — unclassified values dropped via the served `excludedFromEvaluation`.
      performanceCategoryKeys,
      // true only when categoryKeys is [] FOR ROLE-CONFIGURATION REASONS (see above) — stats
      // surfaces read this to render the "Rollenkonfiguration unvollständig" notice instead of a
      // generic empty state.
      vocabularyMissing,
      // True only during the genuine first-fetch window (see above) — the CONFIG-settle signal,
      // not a vocabulary-specific one, though today's consumers are all vocab-derived chrome
      // (treemap, category-noun titles): read this BEFORE vocabularyMissing/categoryLabelsFromData/
      // any label accessor, since none of those has a trustworthy answer yet, and render a shimmer
      // skeleton in the pre-allocated space instead.
      configLoading,
      // VNB: anliegen ids from schema; gMSB: [] (use REASON_CODES instead)
      secondaryAxisKeys,
      // ── The axis nouns, in the three German forms the UI actually needs.
      //
      // Plural and compound EXIST BECAUSE THIS UI ASSEMBLES GERMAN BY CONCATENATION —
      // "{noun}verteilung", "Häufigste {noun}". That held only while every noun was convenient:
      // "Kategorie"/"Marktprozess" need no linking element and pass as plurals. "Bearbeitung" does
      // neither, so glued bare it yields "Bearbeitungverteilung" and "Häufigste Bearbeitung".
      //
      // ONLY the forms that have call sites are exposed, and each legacy fallback is the string
      // that site renders TODAY — not a form derived by rule. Exposing a form nobody uses would
      // mean inventing its legacy value, and the next author would find an untested string sitting
      // there looking authoritative. categoryNounPlural and secondaryAxisNounCompound are deliberately
      // ABSENT for that reason; add them with their site, never ahead of it.
      //
      // ⚠ TRIPWIRE, mirroring the payload's: a FOURTH form means concatenation is exhausted and
      // these sites must take COMPLETE PHRASES instead — not another accessor. German-scoped.

      // VNB: 'Marktprozess'; gMSB/wMSB: 'Kategorie'; other/unresolved: '{category.noun}'
      categoryNoun: vocab?.primary.noun ?? resolveCategoryNoun(schema, role),
      // For sites that GLUE a suffix on: "{…}verteilung", "{…}konfidenz", "Keine {…}daten".
      // 'Kategorie'/'Marktprozess' glue correctly as-is, so their fallback is the base noun.
      categoryNounCompound: vocab?.primary.nounCompound ?? vocab?.primary.noun ?? resolveCategoryNoun(schema, role),
      // VNB: 'Anliegen'; gMSB/wMSB: 'Grund-Code'; other/unresolved: '{anliegen.noun}'
      secondaryAxisNoun: vocab?.secondary?.noun ?? resolveSecondaryAxisNoun(schema, role),
      // For counting sites: "Häufigste …", "Alle …", "Weitere …". ⚠ The legacy fallback is
      // resolveSecondaryAxisNounPlural, NOT the base noun — gMSB renders 'Grund-Codes' there today,
      // so falling back to 'Grund-Code' would silently regress the role this arc must not touch.
      secondaryAxisNounPlural: vocab?.secondary?.nounPlural ?? vocab?.secondary?.noun ?? resolveSecondaryAxisNounPlural(schema, role),
    };
  }, [schema, vocab, vocabLabels, rowLayout, categoryKeys, performanceCategoryKeys, vocabularyMissing, configLoading, secondaryAxisKeys, role]);
}
